// ============================================================
// Console API client. Talks to the Risma API (/v1/admin/*) with the
// httpOnly `risma_admin` session cookie (credentials: 'include').
// Base URL from VITE_RISMA_API_URL; defaults to the local API.
// ============================================================
import type {
  Activity,
  Client,
  ClientInvoice,
  Expense,
  ExpenseInput,
  Integration,
  Lead,
  LeadTouch,
  MessageTemplate,
  OpsData,
  PlanDef,
  ProvisionJob,
  RevPoint,
  VaultEntry,
  VaultKind,
  Workspace,
} from './types';

// Default to the API on the same host the Console is served from (port 3000),
// so the session cookie is same-site in local dev regardless of localhost vs
// 127.0.0.1. Production sets VITE_RISMA_API_URL explicitly (api.rismastudio.it).
const DEFAULT_BASE =
  typeof location !== 'undefined' ? `${location.protocol}//${location.hostname}:3000` : 'http://localhost:3000';

export const API_BASE =
  (import.meta.env.VITE_RISMA_API_URL as string | undefined)?.replace(/\/$/, '') ?? DEFAULT_BASE;

export interface AdminMe {
  id: string;
  email: string;
  name: string | null;
  role: 'operator' | 'partner';
  workspace: { id: string; slug: string; name: string; isHouse: boolean };
}

/** Thrown on a 401 so the store can show the login screen. */
export class UnauthorizedError extends Error {
  constructor() {
    super('unauthenticated');
    this.name = 'UnauthorizedError';
  }
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include' });
  if (res.status === 401) throw new UnauthorizedError();
  if (!res.ok) throw new Error(`${path} → ${res.status}`);
  return (await res.json()) as T;
}

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: 'include',
    ...(body !== undefined ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {}),
  });
  if (res.status === 401) throw new UnauthorizedError();
  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      detail = ((await res.json()) as { error?: string }).error ?? detail;
    } catch {
      /* non-JSON */
    }
    throw new Error(detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
const post = <T>(path: string, body?: unknown) => send<T>('POST', path, body);
const patch = <T>(path: string, body?: unknown) => send<T>('PATCH', path, body);
const del = <T>(path: string) => send<T>('DELETE', path);

export interface NewClientInput {
  name: string;
  slug: string;
  category?: string;
  tier: 'base' | 'completo';
  domain?: string;
  workspace: string;
  ownerName?: string;
  ownerEmail: string;
  /** When converting a won lead → client. */
  leadId?: string;
}

export const api = {
  base: API_BASE,
  me: () => get<AdminMe>('/v1/admin/me'),
  clients: () => get<{ clients: Client[] }>('/v1/admin/clients').then((r) => r.clients),
  client: (id: string) => get<{ client: Client }>(`/v1/admin/clients/${encodeURIComponent(id)}`).then((r) => r.client),
  workspaces: () => get<{ workspaces: Workspace[] }>('/v1/admin/workspaces').then((r) => r.workspaces),
  overview: () => get<{ activity: Activity[]; revenue: RevPoint[] }>('/v1/admin/overview'),
  config: () => get<{ plans: PlanDef[]; integrations: Integration[] }>('/v1/admin/config'),
  impersonate: (id: string, view?: 'website') =>
    post<{ url: string }>(`/v1/admin/clients/${encodeURIComponent(id)}/impersonate${view ? `?view=${view}` : ''}`),

  // ── robot di provisioning (Opzione C) ───────────────────────
  provisionStatus: (slug: string) =>
    get<{ data: ProvisionJob | null }>(`/v1/admin/clients/${encodeURIComponent(slug)}/provision`).then(
      (r) => r.data,
    ),
  /** Accoda provision/rebuild. Su contenuto incompleto lancia un Error con
   *  l'elenco di cosa manca (messaggio già pronto per la UI). */
  provisionStart: async (slug: string, kind: 'provision' | 'rebuild'): Promise<ProvisionJob> => {
    const res = await fetch(`${API_BASE}/v1/admin/clients/${encodeURIComponent(slug)}/provision`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind }),
    });
    if (res.status === 401) throw new UnauthorizedError();
    const body = (await res.json().catch(() => ({}))) as {
      data?: ProvisionJob;
      error?: string;
      missing?: string[];
    };
    if (!res.ok) {
      if (body.error === 'content_incomplete') {
        throw new Error(`Prima di creare il sito manca: ${(body.missing ?? []).join(' · ')}`);
      }
      if (body.error === 'job_in_progress') {
        throw new Error('C’è già un lavoro in corso per questo cliente.');
      }
      if (body.error === 'not_robot_managed') {
        throw new Error('Questo cliente non è gestito dal robot (sito gestito a mano).');
      }
      throw new Error(body.error ?? `HTTP ${res.status}`);
    }
    return body.data!;
  },
  createClient: (input: NewClientInput) => post<{ client: Client }>('/v1/admin/clients', input).then((r) => r.client),
  patchClient: (
    id: string,
    body: {
      monthlyFee?: number | null;
      isDemo?: boolean;
      billingName?: string | null;
      vatNumber?: string | null;
      commercialistaEmail?: string | null;
      ownerEmail?: string;
      archived?: boolean;
      name?: string;
      category?: string | null;
      designStyle?: string;
      template?: string;
      templateConfig?: Record<string, string | boolean>;
      heroLayout?: 'split' | 'cover' | 'type-only' | 'centro' | null;
      sectionNumerals?: boolean | null;
      menuLayout?: string | null;
      marketingDomain?: string;
    },
  ) => patch<{ client: Client }>(`/v1/admin/clients/${encodeURIComponent(id)}`, body).then((r) => r.client),

  /** Eliminazione DEFINITIVA del cliente (cascade su tutte le sue tabelle). */
  deleteClient: (id: string) => del<void>(`/v1/admin/clients/${encodeURIComponent(id)}`),

  // ── invoices (Fase 0: Risma fattura il cliente) ───────────
  invoices: (id: string) =>
    get<{ invoices: ClientInvoice[] }>(`/v1/admin/clients/${encodeURIComponent(id)}/invoices`).then((r) => r.invoices),
  createInvoice: (id: string, body: { period: string; amount: number }) =>
    post<{ invoice: ClientInvoice }>(`/v1/admin/clients/${encodeURIComponent(id)}/invoices`, body).then((r) => r.invoice),
  patchInvoice: (invId: string, status: 'sent' | 'paid') =>
    patch<{ invoice: ClientInvoice }>(`/v1/admin/invoices/${invId}`, { status }).then((r) => r.invoice),

  // ── leads / outreach ──────────────────────────────────────
  leads: () => get<{ leads: Lead[] }>('/v1/admin/leads').then((r) => r.leads),
  leadTemplates: () => get<{ templates: MessageTemplate[] }>('/v1/admin/leads/templates').then((r) => r.templates),
  createTemplate: (body: { channel: 'whatsapp' | 'email'; name: string; subject?: string; body: string }) =>
    post<{ template: MessageTemplate }>('/v1/admin/leads/templates', body).then((r) => r.template),
  updateTemplate: (id: string, body: { channel: 'whatsapp' | 'email'; name: string; subject?: string; body: string }) =>
    patch<{ template: MessageTemplate }>(`/v1/admin/leads/templates/${encodeURIComponent(id)}`, body).then((r) => r.template),
  deleteTemplate: (id: string) => del<void>(`/v1/admin/leads/templates/${encodeURIComponent(id)}`),
  createLead: (input: Partial<Lead>) => post<{ lead: Lead }>('/v1/admin/leads', input).then((r) => r.lead),
  importLeads: (rows: Partial<Lead>[]) =>
    post<{ imported: number; skipped: number; skippedNames: string[]; leads: Lead[] }>('/v1/admin/leads/import', { leads: rows }),
  /** Ricerca automatica per città via OpenStreetMap (attività senza sito). */
  findLeads: (city: string, types?: string[]) =>
    get<{ city: string; found: number; withSite: number; dupes: number; results: { name: string; locality: string; category: string; phone: string | null; email: string | null }[] }>(
      `/v1/admin/leads/find?city=${encodeURIComponent(city)}${types && types.length ? `&types=${encodeURIComponent(types.join(','))}` : ''}`,
    ),
  patchLead: (id: string, p: Partial<Lead>) => patch<{ lead: Lead }>(`/v1/admin/leads/${id}`, p).then((r) => r.lead),
  deleteLead: (id: string) => del<void>(`/v1/admin/leads/${id}`),
  bulkDeleteLeads: (ids: string[]) =>
    post<{ deleted: number }>('/v1/admin/leads/bulk-delete', { ids }).then((r) => r.deleted),
  leadTouches: (id: string) => get<{ touches: LeadTouch[] }>(`/v1/admin/leads/${id}/touches`).then((r) => r.touches),
  addTouch: (id: string, body: { channel: string; note?: string }) =>
    post<{ touch: LeadTouch }>(`/v1/admin/leads/${id}/touches`, body).then((r) => r.touch),
  sendLeadEmail: (id: string, body: { subject: string; body: string }) => post<void>(`/v1/admin/leads/${id}/email`, body),

  // ── operations / monitoring ───────────────────────────────
  monitoring: () => get<OpsData>('/v1/admin/monitoring'),
  runChecks: () => post<{ checked: number; up: number; down: number; skipped: number }>('/v1/admin/monitoring/run'),

  // ── expenses ──────────────────────────────────────────────
  expenses: () => get<{ expenses: Expense[] }>('/v1/admin/expenses').then((r) => r.expenses),
  createExpense: (input: ExpenseInput) => post<{ expense: Expense }>('/v1/admin/expenses', input).then((r) => r.expense),
  patchExpense: (id: string, input: Partial<ExpenseInput>) =>
    patch<{ expense: Expense }>(`/v1/admin/expenses/${id}`, input).then((r) => r.expense),
  deleteExpense: (id: string) => del<void>(`/v1/admin/expenses/${id}`),

  // ── credential vault ──────────────────────────────────────
  vault: () => get<{ secrets: VaultEntry[] }>('/v1/admin/vault').then((r) => r.secrets),
  createSecret: (input: { label: string; kind?: VaultKind; username?: string; secret: string; note?: string; tenantSlug?: string }) =>
    post<{ id: string }>('/v1/admin/vault', input),
  deleteSecret: (id: string) => del<void>(`/v1/admin/vault/${id}`),
  revealSecret: (id: string) => post<{ secret: string; username: string | null }>(`/v1/admin/vault/${id}/reveal`),

  requestMagicLink: (email: string) =>
    fetch(`${API_BASE}/v1/admin/auth/magic-link`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    }),
  logout: () => fetch(`${API_BASE}/v1/admin/auth/logout`, { method: 'POST', credentials: 'include' }),
};
